import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calculator as CalculatorIcon, DollarSign, TrendingUp, Zap } from "lucide-react";

export const Calculator = () => {
  const [studentCount, setStudentCount] = useState<string>("");
  const [schoolType, setSchoolType] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [results, setResults] = useState<any>(null);

  const calculatePotential = () => {
    if (!studentCount || !schoolType || !location) return;

    const students = parseInt(studentCount);
    
    // Base calculations (simplified for demo)
    const baseEnergySize = Math.min(Math.max(students * 2, 50), 500); // kW
    const annualSavings = baseEnergySize * 150; // $150 per kW annually
    const rebates = baseEnergySize * 800; // $800 per kW in rebates
    const totalBenefits = annualSavings + (rebates / 8); // Spread rebates over 8 years
    
    // Adjust for school type and location
    let multiplier = 1;
    if (schoolType === "elementary") multiplier = 0.8;
    if (schoolType === "high") multiplier = 1.2;
    if (location === "rural") multiplier *= 1.1;
    if (location === "urban") multiplier *= 0.9;

    const adjustedBenefits = Math.round(totalBenefits * multiplier);
    const eightYearTotal = Math.round(adjustedBenefits * 8);
    const lifetimeBenefits = Math.round(adjustedBenefits * 25); // 25-year system life

    setResults({
      systemSize: Math.round(baseEnergySize * multiplier),
      annualBenefits: adjustedBenefits,
      eightYearTotal,
      lifetimeBenefits,
      perStudentAnnual: Math.round(adjustedBenefits / students)
    });
  };

  return (
    <section id="calculator" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-green-600 border-green-600">
            <CalculatorIcon className="w-4 h-4 mr-2" />
            Funding Calculator
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Calculate Your PAC's Funding Potential
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get an estimate of how much funding your PAC could generate through 
            the GreenSmart energy partnership program.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900">
                School Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="students" className="text-sm font-medium text-gray-700">
                  Number of Students
                </Label>
                <Input
                  id="students"
                  type="number"
                  placeholder="e.g., 450"
                  value={studentCount}
                  onChange={(e) => setStudentCount(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="school-type" className="text-sm font-medium text-gray-700">
                  School Type
                </Label>
                <Select value={schoolType} onValueChange={setSchoolType}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select school type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="elementary">Elementary School</SelectItem>
                    <SelectItem value="middle">Middle School</SelectItem>
                    <SelectItem value="high">High School</SelectItem>
                    <SelectItem value="k12">K-12 School</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="location" className="text-sm font-medium text-gray-700">
                  Location Type
                </Label>
                <Select value={location} onValueChange={setLocation}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select location type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urban">Urban</SelectItem>
                    <SelectItem value="suburban">Suburban</SelectItem>
                    <SelectItem value="rural">Rural</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={calculatePotential}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                disabled={!studentCount || !schoolType || !location}
              >
                Calculate Funding Potential
              </Button>
            </CardContent>
          </Card>

          {results && (
            <Card className="shadow-lg bg-gradient-to-br from-green-50 to-blue-50">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-gray-900 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                  Your Funding Potential
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <Zap className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">{results.systemSize} kW</div>
                    <div className="text-sm text-gray-600">System Size</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">${results.perStudentAnnual}</div>
                    <div className="text-sm text-gray-600">Per Student/Year</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Annual PAC Benefits</span>
                      <span className="text-xl font-bold text-green-600">
                        ${results.annualBenefits.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">8-Year Total Benefits</span>
                      <span className="text-xl font-bold text-blue-600">
                        ${results.eightYearTotal.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">25-Year Lifetime Benefits</span>
                      <span className="text-xl font-bold text-purple-600">
                        ${results.lifetimeBenefits.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                  <p className="text-sm text-gray-600">
                    <strong>Note:</strong> These are estimated figures based on typical installations. 
                    Actual benefits may vary based on specific site conditions, energy usage, and local utility rates.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
};